import { useEffect, useRef } from 'react'
import L from 'leaflet'
import { OPTIONS_CARTE, ajouterFondDeCarte } from './fondDeCarte'
import type { Commande, Livreur } from '../api/types'
import { fcfa } from './Format'

/**
 * Carte du direct — TRANSPOSITION du gabarit d'origine.
 *
 * ⚠️ Elle manquait ENTIÈREMENT au portage. C'est pourtant l'outil central de
 * l'écran : sans elle, affecter une course revient à choisir un nom dans une
 * liste sans savoir qui est près du marchand.
 *
 * Les couleurs ne sont pas décoratives : or = en course, vert = disponible,
 * carré or = point de retrait, rond rouge = livraison. Un trait plein relie
 * les deux quand un livreur est affecté, pointillé gris sinon — on voit d'un
 * coup d'œil les courses qui attendent encore quelqu'un.
 */
const OR = 'var(--or)'
const VERT = 'var(--vert)'
const ROUGE = 'var(--rouge)'

const echappe = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)

export function CarteLive({
  commandes,
  livreurs,
}: {
  commandes: Commande[]
  livreurs: Livreur[]
}) {
  const hote = useRef<HTMLDivElement>(null)
  const carte = useRef<L.Map | null>(null)
  const couches = useRef<L.Layer[]>([])

  useEffect(() => {
    if (!hote.current || carte.current) return
    carte.current = L.map(hote.current, OPTIONS_CARTE).setView([12.63, -8.0], 13)
    // Fond OpenFreeMap (tuiles vectorielles, sans clé) — voir `fondDeCarte.ts`.
    ajouterFondDeCarte(carte.current)

    /* ⚠️ Leaflet mesure son conteneur UNE FOIS, à la création, et met le
       résultat en cache. Ici React monte la carte avant que la mise en page
       soit stabilisée : mesuré à 469 px pour un conteneur qui en fait 599, il
       ne demandait que deux colonnes de tuiles et laissait une bande grise sur
       la droite.
       Et comme le conteneur ne CHANGE jamais de taille ensuite, aucun
       redimensionnement ne venait le rattraper : ni l'observateur, ni
       l'événement `resize` de la fenêtre. D'où une remesure explicite —
       `pan:false` pour ne pas recentrer la vue au passage. */
    const remesurer = () => carte.current?.invalidateSize({ animate: false, pan: false })
    const observateur = new ResizeObserver(remesurer)
    observateur.observe(hote.current)
    const differee = setTimeout(remesurer, 300)
    return () => {
      clearTimeout(differee)
      observateur.disconnect()
      carte.current?.remove()
      carte.current = null
    }
  }, [])

  useEffect(() => {
    const c = carte.current
    if (!c) return

    /* On RETIRE les couches précédentes avant de redessiner. Les empiler
       laisserait les anciennes positions des livreurs sur la carte : au bout
       d'une heure, le dispatch verrait vingt marqueurs pour deux livreurs. */
    couches.current.forEach((l) => c.removeLayer(l))
    couches.current = []
    c.invalidateSize()

    for (const d of livreurs.filter((l) => l.etat !== 'offline')) {
      const couleur = d.etat === 'busy' ? OR : VERT
      const marqueur = L.circleMarker([d.lat, d.lng], {
        radius: 9,
        fillColor: couleur,
        color: 'var(--surface)',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9,
      }).addTo(c)
      const course = commandes.find(
        (o) => o.livreurId === d.id && !['delivered', 'cancelled'].includes(o.statut),
      )
      marqueur.bindPopup(
        `<div style="font-size:13px;line-height:1.6"><strong>${echappe(d.nom)}</strong><br>
         <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${couleur};margin-right:4px"></span>${d.etat === 'busy' ? 'En course' : 'Disponible'}<br>
         ${echappe(d.typeVehicule ?? '')} — ${echappe(d.plaque)}<br>
         ${course ? `<strong>Commande:</strong> ${echappe(course.ref ?? '')}<br><strong>Client:</strong> ${echappe(course.clientNom)}` : ''}</div>`,
        { className: 'leaflet-popup-driver' },
      )
      // Le prénom seul : un nom complet au-dessus de chaque point rend la
      // carte illisible dès cinq livreurs dans le même quartier.
      marqueur.bindTooltip(
        L.tooltip({ permanent: true, direction: 'top', offset: [0, -12], className: 'driver-label' })
          .setContent(echappe(String(d.nom || '').split(' ')[0] ?? '')),
      )
      couches.current.push(marqueur)
    }

    for (const o of commandes.filter((x) => !['delivered', 'cancelled'].includes(x.statut))) {
      if (o.marchandLat == null || o.clientLat == null) continue

      const iconeMarchand = L.divIcon({
        html: `<div style="width:12px;height:12px;background:${OR};border:2px solid var(--surface);border-radius:2px;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
        className: '',
      })
      const mMarchand = L.marker([o.marchandLat, o.marchandLng ?? 0], { icon: iconeMarchand }).addTo(c)
      mMarchand.bindPopup(
        `<div style="font-size:13px;line-height:1.6"><strong>${echappe(o.marchandNom)}</strong><br>
         <strong>Commande:</strong> ${echappe(o.ref ?? '')}<br>
         <strong>Client:</strong> ${echappe(o.clientNom)}<br>
         <strong>Total:</strong> ${fcfa(o.total)}</div>`,
      )
      couches.current.push(mMarchand)

      const iconeClient = L.divIcon({
        html: `<div style="width:10px;height:10px;background:${ROUGE};border:2px solid var(--surface);border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>`,
        iconSize: [10, 10],
        iconAnchor: [5, 5],
        className: '',
      })
      const mClient = L.marker([o.clientLat, o.clientLng ?? 0], { icon: iconeClient }).addTo(c)
      mClient.bindPopup(
        `<div style="font-size:13px;line-height:1.6"><strong>Livraison: ${echappe(o.clientNom)}</strong><br>${echappe(o.clientQuartier)}<br><strong>Commande:</strong> ${echappe(o.ref ?? '')}</div>`,
      )
      couches.current.push(mClient)

      const trait = L.polyline(
        [
          [o.marchandLat, o.marchandLng ?? 0],
          [o.clientLat, o.clientLng ?? 0],
        ],
        {
          color: o.livreurId ? OR : 'var(--bord)',
          weight: 2,
          opacity: 0.6,
          dashArray: o.livreurId ? undefined : '6 4',
        },
      ).addTo(c)
      couches.current.push(trait)
    }
  }, [commandes, livreurs])

  return (
    <div
      ref={hote}
      style={{ height: 350, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}
    />
  )
}
